import React, { useEffect, useState } from 'react'
import { Grid, Typography } from '@mui/material'
import axios from 'axios'
import ReactGA from 'react-ga4'
import PhoneInput from 'react-phone-input-2'
import { useNavigate } from 'react-router-dom'
import Footer from 'components/Footer'
import Header from 'components/Header'
import SmsConsent, { smsConsentTextShown } from 'components/shared/smsConsent'
import useQueryParams from 'hooks/useQueryParams'
import { SetPhoneNumberSignUp, SetFormattedPhoneNumberSignUp, SetUserIdSignUp } from 'redux/SignUp/persist'
import { useAppDispatch, useAppSelector } from 'redux/store'
import { isAdTrafficSource } from 'utils/adTrafficSource'
import { createNavigateWithQueryParams } from 'utils/navigateWithQueryParams'
import styles from './Phone.module.scss'
import 'react-phone-input-2/lib/material.css'
import { AppConstants, LeadStage } from '../../../AppConstants/AppConstants'

const PhoneSignUp: React.FC = () => {
  const { getAllParams, getParams, hasCampaign, hasContent } = useQueryParams()
  const utm_source = getParams('utm_source')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // Local state
  const [phoneNumber, setPhoneNumber] = useState('')
  const [formattedPhoneNum, setFormattedPhoneNum] = useState('')
  const [disableContinue, setDisableContinue] = useState(true)
  const [errorType, setErrorType] = useState('')
  const [smsConsent, setSmsConsent] = useState(false)
  const [smsMarketingConsent, setSmsMarketingConsent] = useState(false)

  // Paid-ad traffic keeps the pre-A2P inline disclosure: consent is implied by submitting
  const isAdTraffic = isAdTrafficSource(utm_source)
  // Consent is never a condition of signing up (TCR requires it to be optional
  // in practice, not just unchecked), so this feeds the payload only — never
  // the Continue button.
  const hasSmsConsent = isAdTraffic || smsConsent

  // Signup flow state management
  const firstname = useAppSelector((state) => state.signUpPersist.firstname)
  const phoneNumberFromRedux = useAppSelector((state) => state.signUpPersist.phoneNumber)
  const formattedPhoneNumberFromRedux = useAppSelector((state) => state.signUpPersist.formattedPhoneNumber)
  const isMoreThanOneStudio = useAppSelector((state) => state.signUpPersist.isMoreThanOneStudioSignUp)
  const trackerCount = useAppSelector((state) => state.signUpPersist.trackerCountSignUp)

  // Create reusable navigation function
  const navigateWithQueryParams = createNavigateWithQueryParams(navigate, getAllParams)

  const sendOtp = async () => {
    if (phoneNumber.length >= 11 && firstname?.length !== 0) {
      try {
        // Extract query parameters for payload
        const email = localStorage.getItem(AppConstants.USER_EMAIL) || null
        const utm_source = getParams('utm_source') || null
        const utm_medium = getParams('utm_medium') || null
        const utm_campaign = getParams('utm_campaign') || null
        const market = getParams('market') || null
        const loc = getParams('loc') || null
        const utm_content = getParams('utm_content') || null
        const response: any = await axios
          .post(AppConstants.API_URL + '/referral/lead/register/signup', {
            firstname: firstname,
            phone: '+' + phoneNumber.replace(/-/g, ''),
            email: email,
            referralCode: hasCampaign ? utm_campaign : null,
            utm_content: hasContent ? utm_content : null,
            utm_source: utm_source,
            utm_medium: utm_medium,
            utm_campaign: utm_campaign,
            market: market,
            loc: loc,
            smsConsent: hasSmsConsent,
            smsMarketingConsent: smsMarketingConsent,
            smsConsentAt: new Date().toISOString(),
            smsConsentText: smsConsentTextShown({
              legacy: isAdTraffic,
              promotional: smsMarketingConsent,
            }),
          })
          .then((res) => res)
          .catch((err) => err.response)

        if (
          response &&
          response?.data?.code === 'success' &&
          response?.data?.message === 'Verification code sent successfully'
        ) {
          // Store phone number and userId in Redux state
          dispatch(SetPhoneNumberSignUp(phoneNumber))
          dispatch(SetUserIdSignUp(response?.data?.data?.id))
          dispatch(SetFormattedPhoneNumberSignUp(formattedPhoneNum))
          console.log(response, 'response')

          // Navigate to OTP screen
          navigateWithQueryParams('/signup/otp')
        } else if (response && response?.data?.message === 'Phone number is not found') {
          setErrorType('Not_User')
        } else if (response && response.data && response.data.code >= 400 && response.data.code <= 599) {
          setErrorType('Error_Occurred')
        } else if (response && response.data && response.data.statusCode >= 400 && response.data.statusCode <= 599) {
          setErrorType('Error_Occurred')
        }
      } catch (error) {
        console.error('Error sending OTP:', error)
        setErrorType('Error_Occurred')
      }
    } else {
      if (firstname?.length === 0) {
        setErrorType('firstNameEmpty')
      }
      if (phoneNumber.length < 11) {
        setErrorType('formatInvalid')
      }
    }
  }

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (!disableContinue) {
        sendOtp()
      }
    }
  }

  useEffect(() => {
    if (utm_source === 'google') {
      ReactGA.event('page_view', {
        page_title: 'SignUp|Phone',
        page_location: `/signup/phone`,
      })
    }
  }, [utm_source])

  // Populate phone number from Redux state when component mounts
  useEffect(() => {
    if (phoneNumberFromRedux) {
      setPhoneNumber(phoneNumberFromRedux)
      setFormattedPhoneNum(formattedPhoneNumberFromRedux)
    }
  }, [phoneNumberFromRedux, formattedPhoneNumberFromRedux])

  useEffect(() => {
    if (phoneNumber.length >= 11 && firstname && firstname?.length > 0) {
      setDisableContinue(false)
    } else {
      setDisableContinue(true)
    }
  }, [phoneNumber, firstname])

  return (
    <div className={styles.container}>
      <Header
        headerText={''}
        showBackButton={true}
        onBack={() => {
          navigateWithQueryParams('/signup/name')
        }}
      ></Header>
      <div className={`${styles.content} ${isAdTraffic ? styles.centered : ''}`}>
        <div className={styles.textContainer}>
          <span>Nice to meet you, </span>
          <span style={{ textTransform: 'capitalize' }}>{firstname}.</span>
          <br></br>
          <span>What's the best number to </span>
          <br></br>
          <span>reach you at?</span>
        </div>
        <div className={styles.formContainer}>
          <form
            onSubmit={(e) => {
              e.preventDefault()
            }}
          >
            <Grid container>
              <Grid item xs={12}>
                <PhoneInput
                  inputProps={{
                    name: 'phone',
                    autoFocus: true,
                  }}
                  country={'us'}
                  value={phoneNumber}
                  preferredCountries={['us', 'ca', 'gb', 'sa', 'ae']}
                  priority={['us', 'ca', 'gb', 'sa', 'ae']}
                  preserveOrder={['preferredCountries']}
                  containerClass={styles.phoneCtn}
                  dropdownClass={styles.dropdown}
                  specialLabel=""
                  inputClass={styles.input}
                  buttonClass={styles.countryBtn}
                  countryCodeEditable={false}
                  onChange={(value, e: any) => {
                    setPhoneNumber(value)
                    setErrorType('')
                    const formatVal = value.slice(e.dialCode.length)
                    setFormattedPhoneNum(formatVal)
                    if (formatVal.length < 4 || firstname?.length === 0) {
                      setDisableContinue(true)
                    } else {
                      setDisableContinue(false)
                    }
                  }}
                  onKeyDown={handleKeyDown}
                  inputStyle={
                    errorType === 'fieldEmpty' || errorType === 'formatInvalid' || errorType === 'userNot'
                      ? {
                          backgroundImage: "url('images/error.svg')",
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 15px center',
                          textTransform: 'capitalize',
                          border: '1px solid #F24343',
                        }
                      : { border: '1px solid var(--color-border-primary)' }
                  }
                />
              </Grid>
              <SmsConsent
                legacy={isAdTraffic}
                transactional={smsConsent}
                promotional={smsMarketingConsent}
                onTransactionalChange={setSmsConsent}
                onPromotionalChange={setSmsMarketingConsent}
              />
              <Grid item xs={12} sx={{ marginTop: '150px', position: 'absolute', width: '95%' }}>
                {errorType && (
                  <div className={styles.errorContainer}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>
                      {errorType === 'fieldError' && `Please complete all fields above`}
                      {errorType === 'userExists' && `User already exists`}
                      {errorType === 'mobileExists' && `Mobile number already exists`}
                      {errorType === 'unknown' && `Unknown error`}
                      {errorType === 'Incorrect-phoneFormat' && `Invalid Mobile Number`}
                      {errorType === 'Error_Occurred' && `Oops! something went wrong`}
                      {errorType === 'under_18' && `You must be 18 or older to sign up`}
                      {errorType === 'firstNameEmpty' && `Please enter your first name`}
                      {errorType === 'formatInvalid' && `Please enter a valid phone number`}
                      {errorType === 'Not_User' && `Phone number not found`}
                    </Typography>
                    <div
                      style={{
                        display: 'flex',
                        flex: '1',
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                        paddingRight: '12px',
                      }}
                    >
                      <img
                        onClick={() => {
                          setErrorType('')
                        }}
                        style={{ cursor: 'pointer' }}
                        src={'/images/wrong.svg'}
                      ></img>
                    </div>
                  </div>
                )}
              </Grid>
            </Grid>
          </form>
        </div>
      </div>
      <Footer
        buttonText={'Continue'}
        isLoading={false}
        shouldShowFour={true}
        shouldShowFive={true}
        shouldShowSix={true}
        shouldShowSeven={trackerCount >= 7}
        shouldShowEight={trackerCount >= 8}
        trackerIndex={2}
        isDisabled={disableContinue}
        buttonId={'continue-btn'}
        buttonType={'submit'}
        onButtonClick={() => {
          disableContinue ? undefined : sendOtp()
        }}
      ></Footer>
    </div>
  )
}

export default PhoneSignUp
